import { useState } from 'react';
import { useLocation, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { BedDouble, ClipboardList, Clock, NotebookPen, Repeat } from 'lucide-react';
import { IPD_SCHEDULE_DEFAULTS, type CreateIpdOrderInput, type IpdOrderCreated, type IpdOrderKind, type IpdScheduleSettingsView } from '@his/shared';
import { ApiError, get, post } from '@/lib/api';
import { describeError, useAuth } from '@/lib/auth';
import { tr } from '@/lib/i18n-core';
import { display } from '@/lib/utils';
import { FormActions, FormScreen, FormSection, useFormScreen } from '@/components/ui/form-screen';
import { Alert, Button, Field, FormSkeleton, Textarea } from '@/components/ui/primitives';
import { useToast } from '@/components/ui/toast';
import { safeReturnTo } from '@/features/opd/BookAppointmentPage';
import { useAdmission } from '../../admission/queries';
import { admissionIsOpen, ipdEmrPath, type Admission } from '../../admission/types';
import { useIpdEmrForms } from '../IpdEmrPage';
import { defaultIpdForms } from '../ipd-emr-sections';
import { DietFields, NursingFields, OxygenFields, PhysiotherapyFields, VitalsMonitoringFields } from './CareFields';
import { ConsultFields } from './ConsultFields';
import { InstantPicker, PriorityButtons } from './controls';
import type { FieldsProps } from './form-props';
import { IvFluidFields, TransfusionFields } from './InfusionFields';
import { MedicationFields } from './MedicationFields';
import { ORDER_KIND_ICONS, OrderChooser } from './OrderChooser';
import {
  INITIAL_COMMON,
  allergyMatches,
  buildOrderRequest,
  checkDraft,
  chosenOrderKind,
  initialDraft,
  orderChooserEntries,
  schedulePreview,
  touchedFields,
  type CommonDraft,
  type OrderDraft,
  type TypedOrderKind,
} from './order-form';
import { kindLabel, problemText } from './order-labels';
import { SchedulePreviewPanel } from './SchedulePreviewPanel';

const SCHEDULE_SETTINGS_KEY = ['ipd-schedule-settings'] as const;

function useScheduleSettings() {
  return useQuery({
    queryKey: SCHEDULE_SETTINGS_KEY,
    queryFn: () => get<IpdScheduleSettingsView>('/ipd/schedule-settings'),
    staleTime: 600_000,
    retry: 1,
  });
}

function hiddenKinds(hasModule: (module: 'lab' | 'radiology') => boolean): Set<IpdOrderKind> {
  const hidden = new Set<IpdOrderKind>();
  if (!hasModule('lab')) hidden.add('lab');
  if (!hasModule('radiology')) hidden.add('radiology');
  return hidden;
}

export function IpdOrderEntryPage() {
  const { admissionId = '' } = useParams<{ admissionId: string }>();
  const [params, setParams] = useSearchParams();
  const { pathname } = useLocation();
  const { can, hasModule } = useAuth();
  const admission = useAdmission(admissionId);
  const forms = useIpdEmrForms(admissionId);
  const returnTo = safeReturnTo(params.get('returnTo'), ipdEmrPath(admissionId, 'orders'));
  const entries = orderChooserEntries(forms.data?.forms ?? defaultIpdForms(), hiddenKinds(hasModule));
  const kind = chosenOrderKind(params.get('kind'), entries);

  if (admission.isLoading || forms.isLoading) return <FormSkeleton sections={3} className="mx-auto max-w-4xl" />;
  if (!admission.data) return <Alert variant="destructive">{describeError(admission.error) || tr('ipd', 'io_form_load_failed')}</Alert>;

  const record = admission.data;
  const refusal = !admissionIsOpen(record) ? tr('ipd', 'io_form_closed') : !can('cpoe.write') ? tr('ipd', 'io_form_no_permission') : null;
  const chooserParams = new URLSearchParams(params);
  chooserParams.delete('kind');
  const chooserQuery = chooserParams.toString();
  const chooserHref = chooserQuery ? `${pathname}?${chooserQuery}` : pathname;

  if (refusal || !kind) {
    return (
      <FormScreen title={tr('ipd', 'io_form_new_order')} description={tr('ipd', 'io_form_choose_hint')} eyebrow={record.patient.fullName} icon={ClipboardList} backTo={returnTo}>
        <PatientStrip admission={record} />
        {refusal ? (
          <Alert variant="destructive">{refusal}</Alert>
        ) : (
          <OrderChooser
            entries={entries}
            sectionHref={(entry) => ipdEmrPath(record.id, entry.section ?? 'orders')}
            onChoose={(next) =>
              setParams((current) => {
                const updated = new URLSearchParams(current);
                updated.set('kind', next);
                return updated;
              })
            }
          />
        )}
      </FormScreen>
    );
  }

  return <OrderForm key={kind} kind={kind} admission={record} returnTo={returnTo} chooserHref={chooserHref} />;
}

function PatientStrip({ admission }: { admission: Admission }) {
  const bed = admission.bed;
  return (
    <div className="flex flex-wrap items-center gap-x-3 gap-y-1 rounded-xl border bg-card px-4 py-2.5 text-sm shadow-sm">
      <span className="font-semibold">{admission.patient.fullName}</span>
      <span className="font-mono text-xs text-muted-foreground" dir="ltr">
        {admission.patient.mrn}
      </span>
      {bed && (
        <span className="inline-flex items-center gap-1.5 text-muted-foreground">
          <BedDouble className="h-4 w-4" aria-hidden />
          {bed.code} · {bed.room.ward.name}
        </span>
      )}
    </div>
  );
}

function ChangeKindButton({ href }: { href: string }) {
  const { leave } = useFormScreen();
  return (
    <Button type="button" variant="outline" size="sm" onClick={() => leave(href)}>
      <Repeat className="h-4 w-4" aria-hidden /> {tr('ipd', 'io_form_change_kind')}
    </Button>
  );
}

function allergyName(draft: OrderDraft): string {
  if (draft.kind === 'medication') return draft.drugName;
  if (draft.kind === 'iv_fluid') return [draft.fluid, ...draft.additives.map((additive) => additive.name)].join(' ');
  return '';
}

function AllergyStrip({ admission, draft }: { admission: Admission; draft: OrderDraft }) {
  const allergies = admission.patient.allergies;
  if (allergies.length === 0) return null;
  const name = allergyName(draft);
  const matched = new Set(name ? allergies.flatMap((allergy) => allergyMatches(name, [allergy])) : []);
  return (
    <Alert variant={matched.size > 0 ? 'destructive' : 'warning'} title={tr('ipd', matched.size > 0 ? 'io_form_allergy_warning' : 'io_form_allergies')}>
      <ul className="mt-1 flex flex-wrap gap-1.5">
        {allergies.map((allergy) => (
          <li key={allergy.id} className={matched.has(allergy.substance.trim()) ? 'rounded-full bg-destructive/15 px-2 py-0.5 font-semibold' : 'rounded-full bg-background/60 px-2 py-0.5'}>
            {allergy.substance}
            {allergy.reaction ? <span className="font-normal opacity-80"> · {allergy.reaction}</span> : null}
          </li>
        ))}
      </ul>
    </Alert>
  );
}

function KindFields(props: FieldsProps<OrderDraft>) {
  const { draft } = props;
  switch (draft.kind) {
    case 'medication':
      return <MedicationFields {...(props as unknown as FieldsProps<typeof draft>)} draft={draft} />;
    case 'iv_fluid':
      return <IvFluidFields {...(props as unknown as FieldsProps<typeof draft>)} draft={draft} />;
    case 'diet':
      return <DietFields {...(props as unknown as FieldsProps<typeof draft>)} draft={draft} />;
    case 'nursing':
      return <NursingFields {...(props as unknown as FieldsProps<typeof draft>)} draft={draft} />;
    case 'blood_transfusion':
      return <TransfusionFields {...(props as unknown as FieldsProps<typeof draft>)} draft={draft} />;
    case 'oxygen':
      return <OxygenFields {...(props as unknown as FieldsProps<typeof draft>)} draft={draft} />;
    case 'physiotherapy':
      return <PhysiotherapyFields {...(props as unknown as FieldsProps<typeof draft>)} draft={draft} />;
    case 'consult':
      return <ConsultFields {...(props as unknown as FieldsProps<typeof draft>)} draft={draft} />;
    case 'vitals_monitoring':
      return <VitalsMonitoringFields {...(props as unknown as FieldsProps<typeof draft>)} draft={draft} />;
  }
}

interface Failure {
  title: string;
  message: string;
}

function failureOf(caught: unknown): Failure {
  const message = describeError(caught);
  if (caught instanceof ApiError && caught.status === 409) {
    const allergy = caught.details.some((detail) => detail.path === 'allergy');
    return { title: tr('ipd', allergy ? 'io_form_allergy_conflict' : 'io_form_conflict'), message };
  }
  return { title: tr('ipd', 'io_form_save_failed'), message };
}

function OrderForm({ kind, admission, returnTo, chooserHref }: { kind: TypedOrderKind; admission: Admission; returnTo: string; chooserHref: string }) {
  const toast = useToast();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const settings = useScheduleSettings();
  const [common, setCommonState] = useState<CommonDraft>(() => ({ ...INITIAL_COMMON }));
  const [draft, setDraft] = useState<OrderDraft>(() => initialDraft(kind));
  const [touched, setTouched] = useState<ReadonlySet<string>>(() => new Set());
  const [attempted, setAttempted] = useState(false);
  const [failure, setFailure] = useState<Failure | null>(null);

  const touch = (keys: string[]) =>
    setTouched((current) => {
      const next = new Set(current);
      for (const key of touchedFields(keys)) next.add(key);
      return next;
    });
  const update = (patch: Partial<OrderDraft>) => {
    setDraft((current) => ({ ...current, ...patch }) as OrderDraft);
    touch(Object.keys(patch));
  };
  const setCommon = (patch: Partial<CommonDraft>) => {
    setCommonState((current) => ({ ...current, ...patch }));
    touch(Object.keys(patch));
  };

  const save = useMutation({
    mutationFn: (request: CreateIpdOrderInput) => post<IpdOrderCreated>(`/ipd/admissions/${admission.id}/orders`, request),
    onSuccess: (created) => {
      toast.success(tr('ipd', 'io_form_saved'), tr('ipd', 'io_form_saved_detail', { kind: kindLabel(kind), count: created.tasks.length }));
      for (const key of ['ipd-orders', 'ipd-tasks', 'emar', 'admission']) void queryClient.invalidateQueries({ queryKey: [key, admission.id] });
      navigate(returnTo, { replace: true });
    },
    onError: (caught) => setFailure(failureOf(caught)),
  });

  const check = checkDraft(common, draft);
  const timeZone = settings.data?.timeZone || display.timezone;
  const preview = schedulePreview(buildOrderRequest(common, draft), { now: new Date(), timeZone, settings: settings.data ?? IPD_SCHEDULE_DEFAULTS });
  const error = (...fields: string[]) => {
    const problem = check.problems.find((candidate) => fields.includes(candidate.field) && (attempted || touched.has(candidate.field)));
    return problem ? problemText(problem) : undefined;
  };
  const dirty = !save.isSuccess && (JSON.stringify(common) !== JSON.stringify(INITIAL_COMMON) || JSON.stringify(draft) !== JSON.stringify(initialDraft(kind)));

  return (
    <FormScreen
      title={kindLabel(kind)}
      description={tr('ipd', `io_form_desc_${kind}`)}
      eyebrow={tr('ipd', 'io_form_new_order')}
      icon={ORDER_KIND_ICONS[kind]}
      backTo={returnTo}
      dirty={dirty}
      wide
      aside={<SchedulePreviewPanel preview={preview} timeZone={timeZone} />}
      onSubmit={(event) => {
        event.preventDefault();
        setAttempted(true);
        if (!check.request || save.isPending) return;
        setFailure(null);
        save.mutate(check.request);
      }}
    >
      <div className="flex flex-wrap items-center gap-3">
        <div className="min-w-0 flex-1">
          <PatientStrip admission={admission} />
        </div>
        <ChangeKindButton href={chooserHref} />
      </div>
      <AllergyStrip admission={admission} draft={draft} />

      <KindFields draft={draft} update={update} error={error} common={common} setCommon={setCommon} admission={admission} />

      <FormSection title={tr('ipd', 'io_form_section_timing')} icon={Clock}>
        <Field label={tr('ipd', 'io_form_priority')} hint={tr('ipd', 'io_form_priority_hint')} hintBelow>
          <PriorityButtons value={common.priority} onChange={(priority) => setCommon({ priority })} />
        </Field>
        <Field label={tr('ipd', 'io_form_starts_at')} hint={tr('ipd', 'io_form_starts_now_hint')} hintBelow error={error('startsAt')}>
          <InstantPicker value={common.startsAt} onChange={(startsAt) => setCommon({ startsAt })} />
        </Field>
      </FormSection>

      <FormSection title={tr('ipd', 'io_form_instructions')} icon={NotebookPen} columns={1}>
        <Field label={tr('ipd', 'io_form_instructions')} hint={tr('ipd', 'io_form_instructions_hint')} hintBelow error={error('instructions')}>
          <Textarea rows={2} maxLength={1000} value={common.instructions} onChange={(event) => setCommon({ instructions: event.target.value })} />
        </Field>
      </FormSection>

      {failure && (
        <Alert variant="destructive" title={failure.title} className="px-4 py-3 text-base">
          {failure.message}
        </Alert>
      )}

      <div
        className="contents"
        onClickCapture={(event) => {
          if ((event.target as HTMLElement).closest('button[type="submit"]')) setAttempted(true);
        }}
      >
        <FormActions submitLabel={tr('ipd', 'io_form_save')} loading={save.isPending} incomplete={check.problems.length > 0} cancelTo={returnTo} />
      </div>
    </FormScreen>
  );
}
